import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export const COURSE_IDS = {
  FOUNDATIONS_OF_FOCUS: '7aId5DmeqWesCUqC8sqMWa',
  MENTAL_HEALTH_ESSENTIALS: '6a6HNqvFubQlNugmlxtuWx',
}

export const COURSE_NAMES = {
  [COURSE_IDS.FOUNDATIONS_OF_FOCUS]: 'Foundations of Focus',
  [COURSE_IDS.MENTAL_HEALTH_ESSENTIALS]: 'Mental Health Essentials',
}

export interface LessonState {
  completed: boolean | null
  id: string | null
  courseId: string | null
  label: string | null
  v2ImageUrl: string | null
  lessonName: string | null
  duration: number | null
  percentComplete: number | null
  // index of lesson within lessons. used to access lessons vs daily beats from student completion data
  index: number | null
  contentType: string
  // Loaded on detailed lesson request — optional until populated
  sectionId?: string | null
  sectionIndex?: number | null
  numSections?: number | null
  itemIndex?: number | null
  numItems?: number | null
  sectionLabel?: string | null
  currentItem?: any | null
  currentAnswerItem?: any | null
  previousAnswerId?: string | null
  userAnswer?: string | null
  infoPageSummary?: string | null
  lessonNumber?: number | null
  totalItemIndex?: number
  totalNumItems?: number
  isNarrationAvailable?: boolean | null
}

export const selectLessonByCourseId = (courseId: string) => (state: any) => state.lessons.filter((lesson: LessonState) => courseId === lesson.courseId)

export const selectNumLessonsByCourseId = (courseId: string) => (state: any) => state.lessons.filter((lesson: LessonState) => courseId === lesson.courseId).length

export const selectLessonById = (id: string) => (state: any) => state.lessons.find((lesson: LessonState) => id === lesson.id)

export const selectLessons = (state: any) => state.lessons

export const selectComputedPercentComplete = (id: string) => (state: any) => {
  const lesson = state.lessons.find((lesson: LessonState) => id === lesson.id)

  if (!lesson) {
    return 0
  }

  const {
    totalNumItems,
    totalItemIndex,
  } = lesson

  return totalItemIndex / totalNumItems
}

const initialState: LessonState[] = [
  {
    completed: null,
    id: null,
    courseId: null,
    label: null,
    v2ImageUrl: null,
    lessonName: null,
    duration: null,
    percentComplete: null,
    index: null,
    sectionId: null,
    sectionIndex: null,
    numSections: null,
    itemIndex: null,
    numItems: null,
    sectionLabel: null,
    currentItem: null,
    currentAnswerItem: null,
    previousAnswerId: null,
    userAnswer: null,
    contentType: 'lesson',
    infoPageSummary: null,
    isNarrationAvailable: null,
  },
]

const lessonsSlice = createSlice({
  name: 'lessons',
  initialState,
  reducers: {
    loadInitialLessonData: (state, { payload: lessons }: PayloadAction<any[]>) =>
      lessons.map((lesson: any, i: number) => ({
        index: i,
        completed: lesson.completed,
        id: lesson.id,
        courseId: lesson.courseId,
        label: lesson.label,
        v2ImageUrl: lesson.v2ImageUrl?.trim(),
        lessonName: lesson.lessonName,
        duration: lesson.duration,
        percentComplete: lesson.percentComplete,
        contentType: 'lesson',
        lessonNumber: lesson.startDayOffset + 1,
        infoPageSummary: lesson.infoPageSummary,
        isNarrationAvailable: lesson.isNarrationAvailable,
        soundtrackName: lesson.soundtrackName,
      })),
    loadDetailedLessonData: (state, { payload: lesson }: PayloadAction<any>) => {
      // NOTE: this value is only used to override previous answers
      lesson.classroomMode = lesson.classroomMode || false
      const lessonInState = state.find((l) => l.id === lesson.lessonId)
      lessonInState.sectionId = lesson.sectionId
      lessonInState.sectionIndex = lesson.sectionIndex
      lessonInState.numSections = lesson.numSections
      lessonInState.itemIndex = lesson.itemIndex
      lessonInState.numItems = lesson.numItems
      lessonInState.sectionLabel = lesson.sectionLabel
      lessonInState.previousAnswerId = lesson.answerId
      lessonInState.currentItem = lesson.item
      lessonInState.currentAnswerItem = lesson.answerItem
      lessonInState.userAnswer = lesson.classroomMode ? undefined : lesson.userAnswer
      // Note: The backend returns a percentComplete field, which we ignore, because it uses
      // an old way of calculating percent complete based on sections and items. We just
      // use total items.
      lessonInState.percentComplete = lesson.lessonCompleted === true ? 1 : lesson.totalItemIndex / lesson.totalNumItems
      lessonInState.totalItemIndex = lesson.totalItemIndex
      lessonInState.totalNumItems = lesson.totalNumItems
    },
    updatePercentComplete: (state, { payload: { lessonId, percentComplete } }: PayloadAction<{ lessonId: string; percentComplete: number }>) => {
      const lessonInState = state.find((l) => l.id === lessonId)
      lessonInState.percentComplete = percentComplete
      if (percentComplete === 1) {
        lessonInState.completed = true
      }
    },
  },
})

export const { loadInitialLessonData, loadDetailedLessonData, updatePercentComplete } = lessonsSlice.actions

export default lessonsSlice.reducer
